import { z } from 'zod';
import { digestSchema } from '../hashing/hash.js';
import { personIdSchema } from '../people/person.js';
import { uploadMimeSchema } from '../uploads/allowlist.js';
import { fileCategorySchema, fileContainerSchema, fileStoreSchema, isFileLocationFor, } from './categories.js';
/** Generated by the app, never derived from anything a person typed. */
export const fileIdSchema = z.string().uuid();
/** How the bytes reached protected storage (ADR-009 §1). */
export const fileSourceSchema = z.enum(['portal-upload', 'pdf-artifact', 'hr-upload', 'migration']);
const blobNameInputSchema = z
    .object({ personId: personIdSchema, category: fileCategorySchema, sha256: digestSchema })
    .strict();
/**
 * The blob name of a committed file: `{personId}/{category}/{sha256-hex}` (ADR-009 §1).
 *
 * Content-addressed, so a redelivered store effect writes the same name and recognises its own
 * earlier write. It carries NO PII: blob names surface in storage logs, diagnostic exports and
 * error messages, so the person's name, the original file name and the document label live
 * only in Cosmos. Every part is validated rather than trusted — a name-like "person id" or a
 * digest that is not hex throws here instead of becoming a path.
 */
export function blobNameFor(input) {
    const { personId, category, sha256 } = blobNameInputSchema.parse(input);
    return `${personId}/${category}/${sha256.slice('sha256:'.length)}`;
}
/**
 * One committed file (the ADR's FileRecord), in the Cosmos `files` container, pk `/personId`.
 *
 * APPEND-ONLY. A replacement document is a new record; the old one stays, because history is
 * immutable. A correction is a new record too, pointing back with `correctionOf`.
 *
 * `.strict()` is load-bearing: the app never hands out a location a browser could follow, so a
 * document carrying a `url`, `sharePointItemUrl` or any other key nobody declared is refused
 * rather than silently stored. Files are read only through the authenticated API (ADR-009 §4).
 */
export const storedFileSchema = z
    .object({
    id: fileIdSchema,
    docType: z.literal('stored-file'),
    personId: personIdSchema,
    category: fileCategorySchema,
    store: fileStoreSchema,
    container: fileContainerSchema,
    blobName: z.string().min(1),
    /** The immutable blob version the bytes were committed as. */
    versionId: z.string().min(1),
    sha256: digestSchema,
    byteLength: z.number().int().nonnegative(),
    contentType: uploadMimeSchema,
    /** What the person called it. Display only — never a path, never a download name. */
    originalFileName: z.string().min(1),
    source: fileSourceSchema,
    stepInstanceId: z.string().min(1).optional(),
    credentialId: z.string().min(1).optional(),
    uploadId: z.string().min(1).optional(),
    /** Set on the re-commit that corrects a misfiled record (ADR-009 §3a). */
    correctionOf: fileIdSchema.optional(),
    committedAt: z.string().datetime(),
    committedBy: z.string().min(1),
})
    .strict()
    .superRefine((file, ctx) => {
    // The store is DERIVED from the category. Storing both is only safe if they cannot
    // disagree, so a record whose location the category would not produce — above all a
    // segregated category in the records account — does not parse.
    if (!isFileLocationFor(file.category, file)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['container'], message: `${file.category} is never stored in ${file.store}/${file.container}` });
    }
    if (file.correctionOf === file.id) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['correctionOf'], message: 'a file cannot correct itself' });
    }
    if (file.blobName !== blobNameFor({ personId: file.personId, category: file.category, sha256: file.sha256 })) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['blobName'], message: 'blobName must be {personId}/{category}/{sha256-hex}' });
    }
});
/**
 * An HR Admin's record that a committed file is misfiled (ADR-009 §3a step 1, #194).
 *
 * Append-only, in the `files` container under the MISFILED file's person. It never edits the
 * misfiled record: the person file list and viewer derive the exclusion from it.
 */
export const fileCorrectionSchema = z
    .object({
    id: z.string().uuid(),
    docType: z.literal('file-correction'),
    personId: personIdSchema,
    misfiledFileId: fileIdSchema,
    /** The category the file should have had. Omitted when only the person was wrong. */
    targetCategory: fileCategorySchema.optional(),
    /** The person the file belongs to. Omitted when only the category was wrong. */
    targetPersonId: personIdSchema.optional(),
    reason: z.string().trim().min(1).max(1000),
    /** Entra object id of the HR Admin — never their name. */
    recordedBy: z.string().min(1),
    recordedAt: z.string().datetime(),
})
    .strict()
    .refine((correction) => correction.targetCategory !== undefined || correction.targetPersonId !== undefined, {
    message: 'a correction names the correct category, the correct person, or both',
    path: ['targetCategory'],
});
//# sourceMappingURL=stored-file.js.map